import PptxGenJS from "pptxgenjs";
import { SOCIAL_SOURCE_LABEL } from "@/views/marketing/constants/index";

export const TRANSLATIONS = {
  fr: {
    EXEMPLAIRES: "EXEMPLAIRES",
    LECTEURS: "LECTEURS",
    VU: "VU",
    ABONNES_RS: "ABONNÉS RS",
    ABONNES_NEWSLETTER: "ABONNÉS NEWSLETTER",
    PODCAST_IMPRESSIONS: "PODCAST IMPRESSIONS",
    SOURCE: "Source :",
    SOURCES: "Sources :",
    EDITEUR_NEWSLETTER: "Editeur - Newsletter",
    EDITEUR_PODCAST: "Editeur - Podcast",
    FRANCAIS_MOIS: "FRANÇAIS/MOIS",
  },
  en: {
    EXEMPLAIRES: "COPIES",
    LECTEURS: "READERS",
    VU: "UV",
    ABONNES_RS: "SOCIAL\nFOLLOWERS",
    ABONNES_NEWSLETTER: "NEWSLETTER SUBSCRIBERS",
    PODCAST_IMPRESSIONS: "PODCAST IMPRESSIONS",
    SOURCE: "Source:",
    SOURCES: "Sources:",
    EDITEUR_NEWSLETTER: "Publisher - Newsletter",
    EDITEUR_PODCAST: "Publisher - Podcast",
    FRANCAIS_MOIS: "FRENCH/MONTH",
  }
};


export function cmToInches(cm) {
  return cm / 2.54;
}

export function emuToInches(emu) {
  return emu / 914400;
}

export function inchesToCm(inches) {
  return inches * 2.54;
}

export function getImageUrl(path) {
  var baseUrl = process.env.VUE_APP_API_URL || "";
  if (baseUrl.endsWith("/")) {
    baseUrl = baseUrl.slice(0, -1);
  }
  return baseUrl + "/uploads/" + path;
}

export function createPptx(title) {
  const pptx = new PptxGenJS();
  if (title) {
    pptx.title = title;
  }
  pptx.defineLayout({ name: "CUSTOM", width: 13.333, height: 7.5 });
  pptx.layout = "CUSTOM";
  return pptx;
}

export function formatNumberWithSuffix(value, lang = "fr") {
  if (value === null || value === undefined) {
    return { number: "0", suffix: null };
  }

  if (value >= 1000000) {
    var millions = value / 1000000;
    var rounded = Math.round(millions * 10) / 10;
    var formattedStr = rounded.toFixed(1);

    var formatted =
      rounded % 1 === 0
        ? String(rounded)
        : lang === "fr" ? formattedStr.replace(".", ",") : formattedStr;
    return { number: formatted, suffix: "M" };
  }

  if (value >= 1000) {
    var thousands = Math.round(value / 1000);
    return { number: String(thousands), suffix: "K" };
  }

  return { number: String(value), suffix: null };
}

/**
 * Translate source month text into english if requested.
 */
export function translateSourceString(str, lang = "fr") {
  if (!str) return "";
  if (lang === "fr") return str; // Base DB is always FR

  let result = str;
  const monthMap = {
    "Janvier": "January",
    "Février": "February",
    "Mars": "March",
    "Avril": "April",
    "Mai": "May",
    "Juin": "June",
    "Juillet": "July",
    "Août": "August",
    "Septembre": "September",
    "Octobre": "October",
    "Novembre": "November",
    "Décembre": "December",
    "S1": "H1",
    "S2": "H2"
  };

  for (const [fr, en] of Object.entries(monthMap)) {
    const regex = new RegExp(`\\b${fr}\\b`, 'gi');
    result = result.replace(regex, (match) => {
      // Match casing logic
      return match.charAt(0) === match.charAt(0).toUpperCase() ? en : en.toLowerCase();
    });
  }
  return result;
}

/**
 * Format digital_month_data for source text
 * The digital_month_data already contains the best month from the import process
 * @param {string} digitalMonthData - The month data string (e.g., "Juillet 2025")
 * @returns {string} - The formatted month string
 */
export function formatDigitalMonths(digitalMonthData, lang = "fr") {
  if (!digitalMonthData) {
    return "";
  }
  return translateSourceString(digitalMonthData.trim(), lang);
}

/**
 * Check if a value has data (non-zero and not null/undefined)
 * @param {number} value - The value to check
 * @returns {boolean} - True if the value has data
 */
export function hasData(value) {
  if (value === null || value === undefined || value === "") return false;
  return Number(value) !== 0;
}

const METRIC_VALUE_SHIFT_CONFIG = {
  template1: {
    social: { twoOrLessDigits: 0.35, threeDigits: 0.6, default: 0 },
  },
  template2: {
    social: { twoOrLessDigits: -0.55, threeDigits: -0.8, default: 0 },
    newsletter: { twoOrLessDigits: 0, threeDigits: 0.5, default: 0 },
    printCopies: { twoOrLessDigits: 0.45, threeDigits: 0.9, default: 0 },
    printReaders: { twoOrLessDigits: 0.45, threeDigits: 0.9, default: 0 },
  },
  template3: {
    social: { twoOrLessDigits: -0.55, threeDigits: -0.25, default: 0 },
    newsletter: { twoOrLessDigits: 0.7, threeDigits: 1.4, default: 0 },
    digital: { twoOrLessDigits: -0.35, threeDigits: -0.15, default: 0 },
    podcast: { twoOrLessDigits: -0.45, threeDigits: -0.2, default: 0 },
  },
  template4: {
    social: { twoOrLessDigits: -0.55, threeDigits: 1, default: 0 },
  },
};

export function getDisplayedDigitsCount(formattedNumber) {
  return String(formattedNumber).replace(/[^0-9]/g, "").length;
}

export function getMetricValueShift(templateId, metricId, formattedNumber) {
  const metricConfig = METRIC_VALUE_SHIFT_CONFIG[templateId]?.[metricId];
  if (!metricConfig) {
    return 0;
  }

  const digitsCount = getDisplayedDigitsCount(formattedNumber);
  if (digitsCount <= 2) {
    return metricConfig.twoOrLessDigits ?? metricConfig.default ?? 0;
  }

  if (digitsCount === 3) {
    return metricConfig.threeDigits ?? metricConfig.default ?? 0;
  }

  return metricConfig.default ?? 0;
}

const SOURCE_TEXT_RUN_LENGTH_CM = 17;
const SOURCE_TEXT_LINE_HEIGHT_CM = 0.35;
const SOURCE_TEXT_MAX_LINES = 3;
const SOURCE_TEXT_RIGHT_EDGE_CM = 33.55;
const SOURCE_TEXT_BOTTOM_EDGE_CM = 18.8;

export function getSourceTextBox() {
  const runLengthCm = SOURCE_TEXT_RUN_LENGTH_CM;
  const lineStackCm = SOURCE_TEXT_MAX_LINES * SOURCE_TEXT_LINE_HEIGHT_CM;
  const centerXCm = SOURCE_TEXT_RIGHT_EDGE_CM - lineStackCm / 2;
  const centerYCm = SOURCE_TEXT_BOTTOM_EDGE_CM - runLengthCm / 2;

  return {
    w: cmToInches(runLengthCm),
    h: cmToInches(lineStackCm),
    x: cmToInches(centerXCm - runLengthCm / 2),
    y: cmToInches(centerYCm - lineStackCm / 2),
    rotate: 270,
    valign: "bottom",
    align: "left",
    margin: 0,
  };
}

/**
 * Compute metric group slots from right to left within a horizontal zone.
 * Each visible group keeps its own width while hidden groups are removed
 * from the flow entirely.
 * @param {Array} groups - Metric groups with a `visible` flag and `widthCm`
 * @param {object} options - Layout boundaries in centimeters
 * @returns {Array} visible groups with computed `xCm` and `separatorXCm`
 */
export function layoutMetricGroupsRightToLeft(groups, options = {}) {
  const {
    rightEdgeCm = 0,
    gapCm = 0,
    separatorGapCm = 0,
  } = options;

  const visibleGroups = groups.filter((group) => group.visible);
  let cursor = rightEdgeCm;

  return visibleGroups.map((group, index) => {
    const xCm = cursor - group.widthCm;
    const hasSeparator = index < visibleGroups.length - 1;
    const separatorXCm = hasSeparator ? xCm - separatorGapCm : null;

    cursor = hasSeparator ? separatorXCm - gapCm : xCm - gapCm;

    return {
      ...group,
      xCm,
      separatorXCm,
    };
  });
}

/**
 * Compute the real horizontal bounds of a metric group from all child boxes.
 * @param {Array} boxes - Child boxes with `offsetXCm` and `wCm`
 * @returns {object} computed min/max/width in centimeters
 */
export function getMetricGroupBounds(boxes) {
  const minXCm = Math.min(...boxes.map((box) => box.offsetXCm));
  const maxXCm = Math.max(...boxes.map((box) => box.offsetXCm + box.wCm));

  return {
    minXCm,
    maxXCm,
    widthCm: maxXCm - minXCm,
  };
}

/**
 * Place visible metric groups from right to left using their real rendered bounds.
 * The first visible group is aligned so its right-most child box touches `zoneRightCm`.
 * @param {Array} groups - Metric groups with `visible` and `bounds`
 * @param {object} options - Layout options
 * @returns {Array} visible groups with computed anchor and separator positions
 */
export function layoutMetricGroupsByBoundsRightToLeft(groups, options = {}) {
  const {
    zoneRightCm = 0,
    gapCm = 0,
    separatorGapCm = 0,
  } = options;

  const visibleGroups = groups.filter((group) => group.visible);
  let currentRightCm = zoneRightCm;
  let previousGroup = null;

  return visibleGroups.map((group, index) => {
    const anchorXCm =
      previousGroup && previousGroup.leftNeighborAnchorStepCm !== undefined
        ? previousGroup.anchorXCm - previousGroup.leftNeighborAnchorStepCm
        : currentRightCm - group.bounds.maxXCm - (group.rightMarginCm || 0);
    const leftEdgeCm = anchorXCm + group.bounds.minXCm;
    const hasSeparator = index < visibleGroups.length - 1;
    const separatorXCm = hasSeparator
      ? group.separatorOffsetXCm !== undefined
        ? anchorXCm + group.separatorOffsetXCm
        : leftEdgeCm - separatorGapCm
      : null;

    currentRightCm = hasSeparator ? separatorXCm - gapCm : leftEdgeCm - gapCm;
    const positionedGroup = {
      ...group,
      anchorXCm,
      leftEdgeCm,
      rightEdgeCm: anchorXCm + group.bounds.maxXCm,
      separatorXCm,
    };

    previousGroup = positionedGroup;

    return positionedGroup;
  });
}

/**
 * Generate the source text based on template number, settings, and report data
 * Only includes sources when the corresponding data is non-zero
 * @param {number} templateNumber - The template number (1-4)
 * @param {object} settings - The source settings object
 * @param {object} reportData - The brand's report data object
 * @param {string} lang - Language identifier
 * @returns {string} - The complete source text
 */
export function generateSourceText(templateNumber, settings, reportData = {}, lang = "fr") {
  const sources = [];
  const t = TRANSLATIONS[lang];

  switch (templateNumber) {
    case 1:
      // BRAND → audience_brand
      if (
        settings.onenext_global_year &&
        settings.onenext_global_semester &&
        hasData(reportData.audience_brand)
      ) {
        sources.push(
          `ACPM OneNext Global ${settings.onenext_global_year} ${translateSourceString(settings.onenext_global_semester, lang)}`
        );
      }
      // PRINT lecteurs → audience_print_readers
      if (
        settings.onenext_year &&
        settings.onenext_semester &&
        hasData(reportData.audience_print_readers)
      ) {
        sources.push(
          `ACPM OneNext ${settings.onenext_year} ${translateSourceString(settings.onenext_semester, lang)}`
        );
      }
      // PRINT exemplaires → diffusion_print
      if (settings.ojd_dfp_year_range && hasData(reportData.diffusion_print)) {
        sources.push(`ACPM OJD DFP ${settings.ojd_dfp_year_range}`);
      }
      // DIGITAL → audience_digital
      if (
        reportData.digital_month_data &&
        hasData(reportData.audience_digital)
      ) {
        sources.push(
          `Médiamétrie//NetRatings ${formatDigitalMonths(
            reportData.digital_month_data,
            lang
          )}`
        );
      }
      // SOCIAL → social_data
      if (settings.social_index_month && hasData(reportData.social_data)) {
        sources.push(`${SOCIAL_SOURCE_LABEL} ${translateSourceString(settings.social_index_month, lang)}`);
      }
      // NEWSLETTER → newsletter_data
      if (settings.newsletter_month && hasData(reportData.newsletter_data)) {
        sources.push(`${t.EDITEUR_NEWSLETTER} ${translateSourceString(settings.newsletter_month, lang)}`);
      }
      // PODCAST → audio_data
      if (settings.podcast_month && hasData(reportData.audio_data)) {
        sources.push(`${t.EDITEUR_PODCAST} ${translateSourceString(settings.podcast_month, lang)}`);
      }
      break;

    case 2:
      // PRINT lecteurs → audience_print_readers
      if (
        settings.onenext_year &&
        settings.onenext_semester &&
        hasData(reportData.audience_print_readers)
      ) {
        sources.push(
          `ACPM OneNext ${settings.onenext_year} ${translateSourceString(settings.onenext_semester, lang)}`
        );
      }
      // PRINT exemplaires → diffusion_print
      if (settings.ojd_dfp_year_range && hasData(reportData.diffusion_print)) {
        sources.push(`ACPM OJD DFP ${settings.ojd_dfp_year_range}`);
      }
      // SOCIAL → social_data
      if (settings.social_index_month && hasData(reportData.social_data)) {
        sources.push(`${SOCIAL_SOURCE_LABEL} ${translateSourceString(settings.social_index_month, lang)}`);
      }
      // NEWSLETTER → newsletter_data
      if (settings.newsletter_month && hasData(reportData.newsletter_data)) {
        sources.push(`${t.EDITEUR_NEWSLETTER} ${translateSourceString(settings.newsletter_month, lang)}`);
      }
      break;

    case 3:
      // DIGITAL → audience_digital
      if (
        reportData.digital_month_data &&
        hasData(reportData.audience_digital)
      ) {
        sources.push(
          `Médiamétrie//NetRatings ${formatDigitalMonths(
            reportData.digital_month_data,
            lang
          )}`
        );
      }
      // SOCIAL → social_data
      if (settings.social_index_month && hasData(reportData.social_data)) {
        sources.push(`${SOCIAL_SOURCE_LABEL} ${translateSourceString(settings.social_index_month, lang)}`);
      }
      // NEWSLETTER → newsletter_data
      if (settings.newsletter_month && hasData(reportData.newsletter_data)) {
        sources.push(`${t.EDITEUR_NEWSLETTER} ${translateSourceString(settings.newsletter_month, lang)}`);
      }
      // PODCAST → audio_data
      if (settings.podcast_month && hasData(reportData.audio_data)) {
        sources.push(`${t.EDITEUR_PODCAST} ${translateSourceString(settings.podcast_month, lang)}`);
      }
      break;

    case 4:
      // PRINT exemplaires → diffusion_print
      if (settings.ojd_dfp_year_range && hasData(reportData.diffusion_print)) {
        sources.push(`ACPM OJD DFP ${settings.ojd_dfp_year_range}`);
      }
      // SOCIAL → social_data
      if (settings.social_index_month && hasData(reportData.social_data)) {
        sources.push(`${SOCIAL_SOURCE_LABEL} ${translateSourceString(settings.social_index_month, lang)}`);
      }
      break;
  }

  const label = sources.length === 1 ? t.SOURCE : t.SOURCES;
  return label + " " + sources.join(", ");
}
